'use client';

import { Button, Heading, MediaBlock, TextPassage } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import type { ReactElement } from 'react';

import { dataLayerPush } from '@libs/analytics';
import {
    ACTIVATE_OFFER_POST_LOGIN_ACTION,
    formatOfferDate,
    formatStoreEventSchedule,
    isStoreEventOffer,
    useLoyaltyContext,
    useOptimizely,
    usePageLayout,
} from '@libs/common-next';
import { isLocaleCA } from '@libs/core';
import { handleServiceClick } from '@libs/experience-api';

import { AlertBannerContainer } from '../ValidationAlert';
import { OfferCardButton } from './OfferCardButton';
import { useOfferCardActions } from './use-offer-card-actions';

export function OfferCardDetails(): ReactElement | null {
    const { handleActivateOffer, offerPageSection, selectedOffer, showSpinner } = useOfferCardActions();
    const { isLoading } = useLoyaltyContext();
    const { isAuthenticated } = usePageLayout();
    const { context } = useOptimizely();
    const pathname = usePathname();

    if (!selectedOffer) {
        return null;
    }

    // Events show the same schedule as their cards; other offers (or events without a usable start) show expiration.
    const storeEventSchedule = isStoreEventOffer(selectedOffer)
        ? formatStoreEventSchedule(selectedOffer.availabilityStart, selectedOffer.availabilityEnd)
        : '';
    const dateStr = storeEventSchedule || `Exp ${formatOfferDate(selectedOffer.availabilityEnd)}`;
    const showButton = selectedOffer.isActivatable || selectedOffer.isActivated;
    const shopButtonUrl = isLocaleCA() ? selectedOffer.webUrlCa : selectedOffer.webUrl;
    const siteSection = offerPageSection ?? (pathname?.includes('offers') ? 'offers' : 'treats rewards');

    return (
        <div className="c-offer-card-details">
            <AlertBannerContainer />

            <MediaBlock>
                <MediaBlock.Header>
                    <img alt={selectedOffer.shortDescription} src={selectedOffer.webImageUrl} />
                </MediaBlock.Header>
                <MediaBlock.Body>
                    <Heading size="body-lg-bold" tagName="h2">
                        {selectedOffer.shortDescription}
                    </Heading>
                    <TextPassage size="sm">
                        <p>{dateStr}</p>
                    </TextPassage>
                </MediaBlock.Body>
            </MediaBlock>
            <TextPassage className="offer-card-details-description">
                <p>{selectedOffer.longDescription}</p>
            </TextPassage>

            {showButton && (
                <OfferCardButton
                    fullWidth
                    isActivated={selectedOffer.isActivated}
                    loading={showSpinner(selectedOffer) || isLoading}
                    onClick={e => {
                        const element = e.currentTarget as HTMLElement;
                        e.stopPropagation();
                        context?.trackEvent('_optex__loyalty_fly_out_activation');

                        handleActivateOffer(selectedOffer, siteSection, ACTIVATE_OFFER_POST_LOGIN_ACTION);

                        element.blur();
                    }}
                    size="lg"
                />
            )}
            {shopButtonUrl && (
                <Button
                    className="shop-offer-btn"
                    text="Shop this offer"
                    fullWidth
                    onClick={async event => {
                        dataLayerPush({
                            event: 'click_to_shop_offer',
                            selection: selectedOffer.shortDescription.toLowerCase(),
                            page_section: siteSection,
                        });
                        await handleServiceClick({
                            authType: isAuthenticated,
                            event,
                            link: shopButtonUrl,
                        });
                    }}
                />
            )}
        </div>
    );
}
